<template>
  <v-app>

    <!-- App Header -->
    <template v-if="!hideHeader">
      <transition name="fade" appear>
        <app-header />
      </transition>
    </template>

    <!-- Main Navigation -->
    <template v-if="!hideNav">
      <transition name="fade" appear>
        <v-navigation-drawer app width="210" :permanent="isNavOpen" v-model="isNavOpen">
          <main-navigation-sys-admin-ui v-on:navClicked="handleNavClick" v-if="uiType === 'sys-admin'" />
          <main-navigation-shipper-ui v-on:navClicked="handleNavClick" v-else-if="uiType === 'shipper'" />
          <main-navigation-receiver-ui v-on:navClicked="handleNavClick" v-else-if="uiType === 'receiver'" />
        </v-navigation-drawer>
      </transition>
    </template>

    <!-- Active Page -->
    <v-main>
      <!-- Sys Admin Banner -->
      <!-- <template v-if="isAuthenticatedSession && uiType === 'sys-admin'">
        <transition name="fade" appear>
          <div class="banner-sys-admin">SYSTEM ADMIN</div>
        </transition>
      </template> -->

      <!-- Page Content -->
      <div class="page-content-area">
        <transition name="fade-flash" mode="out-in">
          <router-view />
        </transition>
      </div>
    </v-main>

    <!-- Download Manager -->
    <download-manager :style="(showDownloadManager) ? 'display:flex' : 'display:none'" ref="downloadManager" />

    <!-- System Message Dialog -->
    <v-dialog persistent max-width="350" v-model="isSystemMessageDialogOpen">
      <v-card>
        <v-card-text class="pt-6 pb-4 d-flex flex-column align-center justify-center">
          <span class="system-message-text" v-if="systemMessage">{{ systemMessage }}</span>
        </v-card-text>
        <v-card-actions class="pb-5 d-flex flex-column align-center justify-center">
          <v-btn color="primary" @click="closeSystemMessage">
            Ok
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- App Update Notification -->
    <template v-if="isAuthenticatedSession">
      <v-snackbar bottom :value="isAppUpdateAvailable" :timeout="-1" color="secondary">
        <div class="system-message-text pt-3 pb-6">An app update is available!</div>
        <div style="text-align:center" class="pb-2">
          <v-btn color="amber darken-3" @click="refreshApp">Refresh App</v-btn>
        </div>
      </v-snackbar>
    </template>

    <template v-else>
      <v-dialog persistent max-width="350" v-model="isAppUpdateAvailable">
        <v-card class="pt-8 pr-0 pb-8 pb-4">
          <v-card-text class="pt-6 pb-8 d-flex flex-column align-center justify-center">
            <span class="system-message-text">An app update is available!</span>
          </v-card-text>
          <v-card-actions class="pb-5 d-flex flex-column align-center justify-center">
            <v-btn color="primary" @click="refreshApp">
              Refresh App
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>

  </v-app>
</template>

<script>
import AppHeader from '@/components/app/AppHeader'
import MainNavigationSysAdminUi from '@/components/app/MainNavigationSysAdminUi'
import MainNavigationShipperUi from '@/components/app/MainNavigationShipperUi'
import MainNavigationReceiverUi from '@/components/app/MainNavigationReceiverUi'
import DownloadManager from '@/components/app/DownloadManager'

export default {
  name: 'App',

  components: {
    AppHeader,
    MainNavigationSysAdminUi,
    MainNavigationShipperUi,
    MainNavigationReceiverUi,
    DownloadManager
  },

  data () {
    return {
      isNavOpen: true,
      appRegistration: null,
      isAppUpdateAvailable: false,
      isAppUpdating: false,
      isSystemMessageDialogOpen: false,
      systemMessage: null,
      showDownloadManager: false,
      subscriptions: []
    }
  },

  computed: {
    isAuthenticatedSession () {
      return this.$store.getters['auth/isAuthenticated']
    },
    uiType () {
      return this.$root.uiType()
    },
    isMobileView () {
      return (window && window.innerWidth && window.innerWidth < 600)
    },
    hideHeader () {
      return (
        this.$route.name === 'delivery-stepper' ||
        this.$route.name === 'download-order-invoice' ||
        this.$route.name === 'download-route-manifest'
      )
    },
    hideNav () {
      return (
        !this.isAuthenticatedSession ||
        this.$route.name === 'terms-of-use' ||
        this.$route.name === 'register-receiver-account' ||
        this.$route.name === 'claim-account' ||
        this.$route.name === 'accept-invite' ||
        this.$route.name === 'confirm-password-reset' ||
        this.$route.name === 'delivery-stepper' ||
        this.$route.name === 'download-order-invoice' ||
        this.$route.name === 'download-route-manifest'
      )
    }
  },

  beforeMount () {
    // Collapse nav if initialized on mobile size
    if (this.isMobileView) this.isNavOpen = false
  },

  created () {
    // Listen for app updates
    document.addEventListener('swUpdated', this.reportAppUpdate, { once: true })

    // Listen for service worker change
    if (navigator) {
      navigator.serviceWorker.addEventListener(
        'controllerchange', () => {
          if (this.isAppUpdating) return
          this.isAppUpdating = true
          window.location.reload() // reload app
        }
      )
    }

    // Setup subscriptions for data loading
    const unsubscribe = this.$store.subscribe(({ type, payload }) => {
      switch (type) {
        // ------------------------
        // ON: App user logging out
        // ------------------------
        case 'auth/clearActiveUser': {
          // Clear all loaded data in the store
          this.$store.dispatch('sh/clearData')
          break
        }
      }
    })
    this.subscriptions.push(unsubscribe)
  },

  beforeDestroy () {
    // Close subscriptions
    this.removeSubscriptions()
  },

  methods: {
    handleNavClick () {
      // Close nav after click on mobile
      if (this.isMobileView) this.isNavOpen = false
    },

    // Toggle the main navigation component
    toggleMainNav () {
      this.isNavOpen = !this.isNavOpen
    },

    // Sends order invoice download request to the download manager
    // Supports multiple orders
    sendOrderInvoiceRequestToDownloadManager (orders = []) {
      if (!orders) return false
      this.showDownloadManager = true
      this.$refs.downloadManager.setOrderInvoicesToDownload(orders)
    },

    // Sends order attachments download request to the download manager
    // Support a single order
    sendOrderAttachmentsRequestToDownloadManager (order = {}) {
      if (!order || !order.attachment_docs || this.$root.utils.isEmpty(order.attachment_docs)) return false
      this.showDownloadManager = true
      this.$refs.downloadManager.setOrderAttachmentsToDownload(order)
    },

    // Closes the download manager
    closeDownloadManager () {
      this.showDownloadManager = false
    },

    // Shows the "system message" dialog
    revealSystemMessage (message) {
      this.systemMessage = message
      this.isSystemMessageDialogOpen = true
    },

    // Closes the "system message" dialog
    closeSystemMessage () {
      this.isSystemMessageDialogOpen = false
      this.systemMessage = null
    },

    removeSubscriptions () {
      if (this.subscriptions.length > 0) {
        this.subscriptions.forEach((unsubscribe) => unsubscribe())
        this.subscriptions = []
      }
    },

    reportAppUpdate (data) {
      this.appRegistration = data.detail
      this.isAppUpdateAvailable = true
    },

    refreshApp () {
      this.isAppUpdateAvailable = false
      if (!this.appRegistration || !this.appRegistration.waiting) return
      this.appRegistration.waiting.postMessage({ type: 'SKIP_WAITING' })
    }
  }
}
</script>

<style>
  @import "./app.css";

  .banner-sys-admin {
    /* color: #3c3c3c; */
    /* background-color: #F9A825; */
    color: #F9A825;
    border-top: 1px solid #F9A825;
    border-bottom: 1px solid #F9A825;
    font-size: 1.0rem;
    font-weight: 500;
    text-align: center;
    letter-spacing: 2.6px;
    padding: 4px 0;
  }

  .system-message-text {
    font-size: 1.2rem;
    font-weight: 400;
    text-align: center;
  }
</style>
